import type { HelpdeskUnreadCount } from "@repo/shared-types/helpdesk";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useTenantSlug } from "../../shared/web/app-pathname.ts";
import { RequestError } from "../../shared/web/client.ts";
import { NavCount, titleCount, useUnreadRequestCount } from "../../shared/web/nav-counts.tsx";
import { requestLogout, signedOutPath } from "../../shared/web/sign-out.ts";
import { TenantGate } from "../../shared/web/TenantGate.tsx";
import { UnsavedChanges, useUnsavedChangesNavigation } from "../../shared/web/UnsavedChanges.tsx";
import { Button, EmptyState, ErrorText, Feedback } from "../../shared/web/ui/index.ts";
import { externalRequesterApi } from "./requester-api.ts";
import { EXTERNAL_SESSION_KEY, type ExternalSession, externalCatalog, externalIdentity, externalSession } from "./session.ts";
import { trackingAccessDenied } from "./tracking.ts";
import "./external.css";

const SessionContext = createContext<ExternalSession | null>(null);

export function useExternalSession(): ExternalSession {
  const session: ExternalSession | null = useContext(SessionContext);
  if (!session) throw new Error("External session is required");
  return session;
}

export function useExternalRequestError(error: unknown): void {
  const client = useQueryClient();
  useEffect(() => {
    if (error instanceof RequestError && error.status === 401) void client.invalidateQueries({ queryKey: EXTERNAL_SESSION_KEY });
  }, [error, client]);
}

export function useExternalTrackingError(error: unknown): void {
  const client = useQueryClient();
  const session = useExternalSession();
  const identity = externalIdentity(session);
  useExternalRequestError(error);
  useEffect(() => {
    if (!trackingAccessDenied(error)) return;
    const queryKey = ["external-data", identity, "requests"];
    void client.cancelQueries({ queryKey }).then(() => client.removeQueries({ queryKey }));
  }, [client, error, identity]);
}

export function ExternalAccessUnavailable(): ReactNode {
  return (
    <section className="stack">
      <h1 className="t-display">Helpdesk access unavailable</h1>
      <p className="t-body measure">Your company’s Helpdesk access is unavailable for this account. Contact your company’s Helpdesk administrator.</p>
    </section>
  );
}

function ExternalHeader({
  principal,
  loggingOut,
  unread,
  onSignOut,
}: {
  principal: ExternalSession | undefined;
  loggingOut: boolean;
  unread: HelpdeskUnreadCount | null;
  onSignOut: () => void;
}): ReactNode {
  return (
    <div className="app-shell-header">
      <header className="app-header external-header">
        <span className="app-header__brand">{principal?.kind === "external" ? principal.tenantName : "Helpdesk"}</span>
        {principal?.kind === "external" && !loggingOut && (
          <nav aria-label="Helpdesk" className="external-navigation">
            <Link to="/helpdesk" className="nav-link" activeOptions={{ exact: true }}>
              Helpdesk
            </Link>
            <Link to="/helpdesk/requests" className="nav-link">
              Requests
              <NavCount count={unread} />
            </Link>
            <Link to="/profile" className="nav-link">
              Account
            </Link>
          </nav>
        )}
        {principal && (
          <Button variant="ghost" onClick={onSignOut} disabled={loggingOut}>
            {loggingOut ? "Signing out…" : "Sign out"}
          </Button>
        )}
      </header>
    </div>
  );
}

function ExternalShell(): ReactNode {
  const client: QueryClient = useQueryClient();
  const slug: string = useTenantSlug();
  const { confirmLeave, allowUnload } = useUnsavedChangesNavigation();
  const session = useQuery({ queryKey: EXTERNAL_SESSION_KEY, queryFn: ({ signal }) => externalSession(signal), refetchInterval: 60_000, refetchOnWindowFocus: "always" });
  const previousIdentity = useRef<string | null>(null);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const [logoutError, setLogoutError] = useState<boolean>(false);
  const principal: ExternalSession | undefined = session.isError ? undefined : session.data;
  const identity: string | null = principal ? externalIdentity(principal) : null;
  const unread = useUnreadRequestCount(() => externalRequesterApi.unreadCount(), ["external-data", identity, "requests"], principal?.kind === "external" && !loggingOut);

  useEffect(() => {
    if (identity !== previousIdentity.current) client.removeQueries({ predicate: (query) => query.queryKey[0] === "external-data" && query.queryKey[1] !== identity });
    previousIdentity.current = identity;
  }, [client, identity]);

  async function signOut(): Promise<void> {
    if (!confirmLeave()) return;
    setLoggingOut(true);
    setLogoutError(false);
    await client.cancelQueries();
    try {
      await requestLogout();
      allowUnload();
      window.location.assign(signedOutPath(slug));
    } catch {
      setLogoutError(true);
      setLoggingOut(false);
      await session.refetch();
    }
  }

  return (
    <div className="app-main">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <TenantGate header={<ExternalHeader principal={principal} loggingOut={loggingOut} unread={unread} onSignOut={() => void signOut()} />} titleCount={titleCount(unread)}>
        {logoutError && <ErrorText>Could not sign out. Try again.</ErrorText>}
        {loggingOut || !principal ? (
          <Feedback>{loggingOut ? "Signing out…" : "Loading Helpdesk…"}</Feedback>
        ) : (
          <SessionContext.Provider value={principal} key={identity}>
            <Outlet />
          </SessionContext.Provider>
        )}
      </TenantGate>
    </div>
  );
}

export function ExternalLayout(): ReactNode {
  const [client] = useState<QueryClient>(() => new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 0 } } }));
  useEffect(() => () => client.clear(), [client]);
  return (
    <QueryClientProvider client={client}>
      <UnsavedChanges>
        <ExternalShell />
      </UnsavedChanges>
    </QueryClientProvider>
  );
}

export function ExternalHelpdesk(): ReactNode {
  const session: ExternalSession = useExternalSession();
  const client: QueryClient = useQueryClient();
  const catalog = useQuery({ queryKey: ["external-data", externalIdentity(session), "catalog"], queryFn: ({ signal }) => externalCatalog(signal) });
  useEffect(() => {
    if (catalog.error instanceof RequestError && catalog.error.status === 401) void client.invalidateQueries({ queryKey: EXTERNAL_SESSION_KEY });
  }, [catalog.error, client]);
  if (catalog.error instanceof RequestError && catalog.error.status === 403) return <ExternalAccessUnavailable />;
  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="t-display">Helpdesk</h1>
          <p className="t-body">Support for {session.tenantName}.</p>
        </div>
      </div>
      {catalog.isPending ? (
        <Feedback>Loading Helpdesk…</Feedback>
      ) : catalog.isError ? (
        <div className="stack">
          <ErrorText aria-label="Helpdesk catalog load error">Could not load Helpdesk forms.</ErrorText>
          <div>
            <Button onClick={() => void catalog.refetch()}>Try again</Button>
          </div>
        </div>
      ) : catalog.data.categories.length === 0 ? (
        <EmptyState aria-label="Helpdesk catalog empty" title="No forms available yet">
          Your company’s Helpdesk forms will appear here when they are ready. Contact your company’s Helpdesk administrator if you need help.
        </EmptyState>
      ) : (
        <div className="helpdesk-catalog-groups">
          {(catalog.data.groups ?? [{ id: "departments", name: "Departments", categories: catalog.data.categories }]).map((group) => (
            <section className="helpdesk-catalog-group" key={group.id}>
              <h2 className="t-headline">{group.name}</h2>
              <ul className="helpdesk-catalog-grid">
                {group.categories.map((category) => (
                  <li key={category.id}>
                    <Link to="/helpdesk/categories/$departmentId" params={{ departmentId: category.id }} className="card card--interactive helpdesk-catalog-card">
                      <span className="helpdesk-catalog-card__content">
                        <span className="t-title">{category.name}</span>
                        {category.description && <span className="t-body-sm">{category.description}</span>}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExternalAccount(): ReactNode {
  const session: ExternalSession = useExternalSession();
  return (
    <div className="stack">
      <h1 className="t-display">Account</h1>
      <dl className="external-account">
        <div>
          <dt className="t-caption">Name</dt>
          <dd>{session.name}</dd>
        </div>
        <div>
          <dt className="t-caption">Email</dt>
          <dd>{session.email}</dd>
        </div>
        <div>
          <dt className="t-caption">Company</dt>
          <dd>{session.tenantName}</dd>
        </div>
      </dl>
    </div>
  );
}
